import { type InteractionMode } from "@design-lab/entities/artwork";
import type Konva from "konva";
import { useCallback, useEffect, useRef, type PropsWithChildren } from "react";
import { Stage, type StageProps } from "react-konva";
import { useHotjarCanvasCapture } from "../hooks/useHotjarCanvasCapture";
import { useRefinePanAndZoom } from "../hooks/useRefinePanAndZoom";

export type RefineResponsiveStageProps = {
  dimensions: {
    width: number;
    height: number;
  };
  setDimensions: (dimensions: { width: number; height: number }) => void;
  zoomCenter?: { x: number; y: number };
  contentBounds?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  interactionMode: InteractionMode;
  isSpaceHeld: boolean;
  minZoom?: number;
  maxZoom?: number;
};

export function RefineResponsiveStage({
  dimensions,
  setDimensions,
  zoomCenter,
  contentBounds,
  interactionMode,
  isSpaceHeld,
  minZoom = 1,
  maxZoom = 10,
  children,
  ...props
}: PropsWithChildren<RefineResponsiveStageProps & StageProps>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const hotjarImgRef = useHotjarCanvasCapture(stageRef);

  const {
    cursor,
    handleMouseMove,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleMouseLeave,
    handleWheel,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  } = useRefinePanAndZoom({
    dimensions,
    zoomCenter,
    stageRef,
    contentBounds,
    interactionMode,
    isSpaceHeld,
    minZoom,
    maxZoom
  });

  // Function to handle resize
  const updateSize = useCallback(() => {
    if (!containerRef.current) return;

    // Get container dimensions
    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;

    // Update state with container dimensions directly
    setDimensions({
      width: containerWidth,
      height: containerHeight
    });
  }, [setDimensions]);

  // Wrap handleTouchMove to pass containerRef
  const wrappedHandleTouchMove = useCallback(
    (e: Konva.KonvaEventObject<TouchEvent>) => {
      handleTouchMove(e, containerRef);
    },
    [handleTouchMove]
  );

  // Update on mount and observe container size changes
  useEffect(() => {
    updateSize();

    if (!containerRef.current) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => updateSize());
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateSize]);

  return (
    <div ref={containerRef} className="relative flex size-full">
      <img
        ref={hotjarImgRef}
        aria-hidden={true}
        alt=""
        className="pointer-events-none absolute inset-0 size-full"
      />
      <Stage
        ref={stageRef}
        width={dimensions.width}
        height={dimensions.height}
        className="absolute size-full"
        style={{ cursor }}
        onMouseDown={handleDragStart}
        onMouseMove={e => {
          handleMouseMove(e);
          handleDragMove(e);
        }}
        onMouseUp={handleDragEnd}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={wrappedHandleTouchMove}
        onTouchEnd={handleTouchEnd}
        {...props}
      >
        {children}
      </Stage>
    </div>
  );
}
